"use client";

import { addDays } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from "react";

import type { AvailabilityAppointmentType, AvailabilityResponse } from "@/shared";
import { DetailsStep } from "@/src/components/online-scheduling/details-step";
import { ProgressSteps } from "@/src/components/online-scheduling/progress-steps";
import {
  LocationSelectionField,
  ProviderSelectionField,
} from "@/src/components/online-scheduling/provider-location-fields";
import { ReviewStep } from "@/src/components/online-scheduling/review-step";
import { SchedulingErrorAlert } from "@/src/components/online-scheduling/scheduling-error-alert";
import { StepActions } from "@/src/components/online-scheduling/step-actions";
import { SuccessStep } from "@/src/components/online-scheduling/success-step";
import {
  appointmentTypeForPatient,
  EMPTY_PATIENT_DETAILS,
  sanitizeReasonForVisit,
  SCHEDULING_STEPS,
  SchedulingStep,
  type PatientDetailsDraft,
  type OnlineSchedulingLocation,
  type OnlineSchedulingProvider,
  type SchedulingStepId,
} from "@/src/components/online-scheduling/types";
import { VisitStep } from "@/src/components/online-scheduling/visit-step";
import { AppointmentSlotPicker } from "@/src/components/scheduling/appointment-slot-picker";
import { Alert, AlertDescription, AlertTitle } from "@/src/components/shadcn/ui/alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardContent } from "@/src/components/shadcn/ui/card";
import { Label } from "@/src/components/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/src/components/shadcn/ui/tabs";
import { ValerieCard } from "@/src/components/valerie-card";
import {
  bookOnlineSchedulingAppointment,
  checkOnlineSchedulingEligibility,
  fetchOnlineSchedulingAvailability,
  fetchOnlineSchedulingOptions,
} from "@/src/lib/scheduling/online-scheduling-client";
import {
  onlineSchedulingIntakeSchema,
  type OnlineSchedulingBookRequest,
  type OnlineSchedulingIntake,
  type OnlineSchedulingReferralWindow,
  type OnlineSchedulingRegion,
  type OnlineSchedulingBookResponse,
} from "@/src/lib/scheduling/online-scheduling-schemas";
import {
  dateFromKey,
  dateKeyFromDate,
  normalizeAvailabilitySlot,
  type NormalizedSlot,
} from "@/src/lib/scheduling/slot-display";
import { getCurrentIsoDateInTimezone, isMinorDateOfBirth } from "@/src/lib/utils/date-and-phone-utils";

const INITIAL_WINDOW_DAYS = 30;
const WINDOW_INCREMENT_DAYS = 30;
const MAX_WINDOW_DAYS = 120;
const DETAILS_FORM_ID = "scheduling-details-form";

enum EligibilityError {
  Ineligible = "ineligible",
  Unavailable = "unavailable",
}

enum SchedulingMode {
  Location = "location",
  Provider = "provider",
}

const STEP_TITLE: Record<SchedulingStepId, string> = {
  [SchedulingStep.VISIT]: "Choose your provider or location",
  [SchedulingStep.TIME]: "Pick a time",
  [SchedulingStep.DETAILS]: "Your details",
  [SchedulingStep.REVIEW]: "Review & confirm",
};

function availabilitySlotKey(slot: AvailabilityResponse["slots"][number]): string {
  return `${slot.locationId ?? "unknown"}:${slot.appointmentId}`;
}

type PortalProps = ComponentProps<typeof OnlineSchedulingFlow> & {
  regions: OnlineSchedulingRegion[];
  regionSelectionRequired: boolean;
};

export function OnlineSchedulingPortal({ regions, regionSelectionRequired, ...flowProps }: PortalProps) {
  const [isBooking, setIsBooking] = useState(false);
  const selectedRegion = regions.find((region) => region.regionCode === flowProps.regionCode);

  function selectRegion(regionCode: string) {
    if (
      isBooking ||
      regionCode === flowProps.regionCode ||
      !regions.some((region) => region.regionCode === regionCode)
    ) {
      return;
    }

    const query = new URLSearchParams({ regionCode });

    if (flowProps.tenant) query.set("tenant", flowProps.tenant);

    // Reload server-owned options and reset all selections and patient details for the new region.
    window.location.assign(`/schedule?${query}`);
  }

  return (
    <div className="space-y-6">
      {regionSelectionRequired && !selectedRegion && (
        <div className="space-y-2">
          <Label htmlFor="scheduling-region">Region</Label>
          <Select value={flowProps.regionCode ?? ""} onValueChange={selectRegion} disabled={isBooking}>
            <SelectTrigger id="scheduling-region" className="bg-background">
              <SelectValue placeholder="Choose a region" />
            </SelectTrigger>
            <SelectContent>
              {regions.map((region) => (
                <SelectItem key={region.regionCode} value={region.regionCode}>
                  {region.displayName}
                  {region.stateNames?.length ? ` - ${region.stateNames.join(", ")}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {flowProps.initialLoadError ? (
        <div role="alert" className="space-y-3">
          <SchedulingErrorAlert clinicPhoneNumber={selectedRegion?.clinicPhoneNumber ?? flowProps.clinicPhoneNumber} />
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      ) : regionSelectionRequired && !selectedRegion ? (
        <p className="text-sm text-muted-foreground">Choose a region to see its providers and locations.</p>
      ) : (
        <OnlineSchedulingFlow
          {...flowProps}
          brandName={selectedRegion?.displayName ?? flowProps.brandName}
          clinicTimezone={selectedRegion?.timezone ?? flowProps.clinicTimezone}
          clinicPhoneNumber={selectedRegion?.clinicPhoneNumber ?? flowProps.clinicPhoneNumber}
          onBookingStateChange={setIsBooking}
        />
      )}
    </div>
  );
}

export function OnlineSchedulingFlow({
  initialLocations,
  initialProviders,
  initialLocationId,
  initialProviderId,
  tenant,
  initialLoadError,
  clinicTimezone,
  clinicPhoneNumber,
  requireReasonForVisit = false,
  requireInsurance = false,
  allowSelfPay = false,
  requireExistingPatient = false,
  checkSchedulingEligibility = false,
  regionCode = null,
  onBookingStateChange,
}: {
  initialLocations: OnlineSchedulingLocation[];
  initialProviders: OnlineSchedulingProvider[];
  initialLocationId?: string | null;
  initialProviderId?: string | null;
  tenant?: string | null;
  initialLoadError?: boolean;
  brandName?: string;
  clinicTimezone?: string | null;
  clinicPhoneNumber?: string | null;
  /** Per-tenant policy from `getOnlineSchedulingConfig`; blocks patient details until the reason is filled. */
  requireReasonForVisit?: boolean;
  requireInsurance?: boolean;
  allowSelfPay?: boolean;
  requireExistingPatient?: boolean;
  checkSchedulingEligibility?: boolean;
  regionCode?: string | null;
  onBookingStateChange?: (isBooking: boolean) => void;
}) {
  const findInitialLocation = useCallback(
    (list: OnlineSchedulingLocation[]): OnlineSchedulingLocation | null => {
      const matched = initialLocationId
        ? list.find((location) => location.locationId === initialLocationId || location.ehrId === initialLocationId)
        : undefined;

      return matched ?? list[0] ?? null;
    },
    [initialLocationId]
  );
  const findInitialProvider = useCallback(
    (list: OnlineSchedulingProvider[]): OnlineSchedulingProvider | null => {
      if (!initialProviderId) {
        return null;
      }

      return (
        list.find(
          (provider) => provider.practitionerId === initialProviderId || provider.ehrId === initialProviderId
        ) ?? null
      );
    },
    [initialProviderId]
  );

  const [schedulingMode, setSchedulingMode] = useState<SchedulingMode>(
    initialProviderId ? SchedulingMode.Provider : SchedulingMode.Location
  );
  const [locations, setLocations] = useState<OnlineSchedulingLocation[]>(initialLocations);
  const [providers, setProviders] = useState<OnlineSchedulingProvider[]>(initialProviders);
  const lockProvider = requireExistingPatient && findInitialProvider(providers) !== null;
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(
    initialLoadError ? "We couldn't load our providers and clinic locations." : null
  );

  const [selectedLocation, setSelectedLocation] = useState<OnlineSchedulingLocation | null>(() =>
    findInitialLocation(initialLocations)
  );
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(
    () => findInitialProvider(initialProviders)?.practitionerId
  );
  const [locationProviderFilterId, setLocationProviderFilterId] = useState<string | null>(null);
  const [step, setStep] = useState<SchedulingStep>(SCHEDULING_STEPS[0]);

  const [referralWindow, setReferralWindow] = useState<OnlineSchedulingReferralWindow | null>(null);
  const [verifiedPatientDetails, setVerifiedPatientDetails] = useState<OnlineSchedulingIntake | undefined>();
  const maxWindowDays = referralWindow?.maxWindowDays ?? MAX_WINDOW_DAYS;
  const [isReturningPatient, setIsReturningPatient] = useState(false);
  const [reasonForVisit, setReasonForVisit] = useState("");

  const appointmentType: AvailabilityAppointmentType = appointmentTypeForPatient(isReturningPatient);

  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(INITIAL_WINDOW_DAYS);
  const [loadedBaseKey, setLoadedBaseKey] = useState<string | null>(null);
  const availabilityRequestId = useRef(0);

  const [selectedSlot, setSelectedSlot] = useState<AvailabilityResponse["slots"][number] | null>(null);

  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<EligibilityError | null>(null);
  const [details, setDetails] = useState<PatientDetailsDraft>(EMPTY_PATIENT_DETAILS);

  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingResult, setBookingResult] = useState<OnlineSchedulingBookResponse | null>(null);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    setOptionsError(null);

    try {
      const response = await fetchOnlineSchedulingOptions(tenant, regionCode);

      setLocations(response.locations);
      setProviders(response.providers);
      setSelectedLocation((current) => {
        const refreshed = current
          ? response.locations.find((location) => location.locationId === current.locationId)
          : undefined;

        if (refreshed) {
          return refreshed;
        }

        return findInitialLocation(response.locations);
      });
      setSelectedProviderId((current) => {
        if (current && response.providers.some((provider) => provider.practitionerId === current)) {
          return current;
        }

        return findInitialProvider(response.providers)?.practitionerId;
      });
    } catch {
      setOptionsError("We couldn't load our providers and clinic locations. Please try again.");
    } finally {
      setOptionsLoading(false);
    }
  }, [tenant, regionCode, findInitialLocation, findInitialProvider]);

  const selectedProvider = useMemo(
    () =>
      selectedProviderId
        ? (providers.find((provider) => provider.practitionerId === selectedProviderId) ?? null)
        : null,
    [providers, selectedProviderId]
  );

  useEffect(() => {
    if (selectedProviderId && !selectedProvider) {
      setSelectedProviderId(undefined);
    }
  }, [selectedProvider, selectedProviderId]);

  const availabilityScopeKey =
    schedulingMode === SchedulingMode.Location
      ? selectedLocation
        ? `location:${selectedLocation.locationId}`
        : null
      : selectedProviderId
        ? `provider:${selectedProviderId}`
        : null;

  // Reset availability whenever the active search scope or visit type changes so stale slots can
  // never win a race. The inactive mode's selection deliberately does not invalidate the search.
  useEffect(() => {
    availabilityRequestId.current += 1;
    setAvailability(null);
    setAvailabilityLoading(false);
    setIsLoadingMore(false);
    setAvailabilityError(null);
    setLoadedBaseKey(null);
    setSelectedSlot(null);
    setWindowDays(INITIAL_WINDOW_DAYS);
  }, [availabilityScopeKey, isReturningPatient]);

  const loadAvailability = useCallback(
    async (days: number, opts?: { more?: boolean }) => {
      if (!availabilityScopeKey) {
        return;
      }

      const baseKey = `${availabilityScopeKey}:${appointmentType}`;
      const requestId = ++availabilityRequestId.current;

      if (opts?.more) {
        setIsLoadingMore(true);
      } else {
        setAvailabilityLoading(true);
        // Mark this (location, visit-type) as attempted up front so a failed fetch doesn't
        // re-trigger the effect in a loop; the error state offers an explicit "Try again".
        setLoadedBaseKey(baseKey);
      }

      setAvailabilityError(null);

      try {
        // Anchor the window on the clinic's "today" (not the visitor's browser day) so the server's
        // timezone-aware past-date check agrees with what we request.
        const fromDate = getCurrentIsoDateInTimezone(clinicTimezone);
        const searchDays = Math.min(days, maxWindowDays);
        const response = await fetchOnlineSchedulingAvailability(
          {
            location: schedulingMode === SchedulingMode.Location ? selectedLocation?.locationId : null,
            appointmentType,
            patientDetails: referralWindow ? verifiedPatientDetails : undefined,
            fromDate: referralWindow && fromDate < referralWindow.fromDate ? referralWindow.fromDate : fromDate,
            toDate: dateKeyFromDate(addDays(dateFromKey(fromDate), searchDays)),
            regionCode,
            practitionerIds:
              schedulingMode === SchedulingMode.Provider && selectedProviderId
                ? [selectedProviderId]
                : checkSchedulingEligibility
                  ? providers.map((provider) => provider.practitionerId)
                  : undefined,
          },
          tenant
        );

        if (requestId !== availabilityRequestId.current) {
          return;
        }

        setAvailability(response);
        setWindowDays(searchDays);
        setLoadedBaseKey(baseKey);
      } catch {
        if (requestId === availabilityRequestId.current) {
          setAvailabilityError(
            "We couldn't load appointment times. Please refresh the page or call the clinic to book."
          );
        }
      } finally {
        if (requestId === availabilityRequestId.current) {
          if (opts?.more) {
            setIsLoadingMore(false);
          } else {
            setAvailabilityLoading(false);
          }
        }
      }
    },
    [
      availabilityScopeKey,
      referralWindow,
      verifiedPatientDetails,
      maxWindowDays,
      appointmentType,
      schedulingMode,
      selectedLocation,
      selectedProviderId,
      tenant,
      clinicTimezone,
      regionCode,
      checkSchedulingEligibility,
      providers,
    ]
  );

  // Fetch availability when the patient reaches the time step (or changes the active search scope / visit type).
  useEffect(() => {
    if (step !== SchedulingStep.TIME || !availabilityScopeKey) {
      return;
    }

    const baseKey = `${availabilityScopeKey}:${appointmentType}`;

    if (loadedBaseKey === baseKey || availabilityLoading) {
      return;
    }

    void loadAvailability(INITIAL_WINDOW_DAYS);
  }, [step, availabilityScopeKey, appointmentType, loadedBaseKey, availabilityLoading, loadAvailability]);

  const locationProviderOptions = useMemo(() => {
    const availableProviderIds = new Set(
      (availability?.slots ?? []).flatMap((slot) => (slot.practitionerId ? [slot.practitionerId] : []))
    );

    return providers.filter((provider) => availableProviderIds.has(provider.practitionerId));
  }, [availability, providers]);

  useEffect(() => {
    if (
      locationProviderFilterId &&
      !locationProviderOptions.some((provider) => provider.practitionerId === locationProviderFilterId)
    ) {
      setLocationProviderFilterId(null);
      setSelectedSlot(null);
    }
  }, [locationProviderFilterId, locationProviderOptions]);

  const visibleSlots = useMemo(() => {
    const slots = (availability?.slots ?? []).filter(
      (slot) =>
        !checkSchedulingEligibility ||
        (providers.some((provider) => provider.practitionerId === slot.practitionerId) &&
          locations.some((location) => location.locationId === slot.locationId))
    );

    if (schedulingMode !== SchedulingMode.Location || !locationProviderFilterId) {
      return slots;
    }

    return slots.filter((slot) => slot.practitionerId === locationProviderFilterId);
  }, [availability, locationProviderFilterId, schedulingMode, checkSchedulingEligibility, providers, locations]);

  const locationDisplayNameById = useMemo(
    () => new Map(locations.map((location) => [location.locationId, location.displayName])),
    [locations]
  );
  const normalizedSlots = useMemo<NormalizedSlot[]>(() => {
    return visibleSlots.map((slot, index) => ({
      ...normalizeAvailabilitySlot(
        slot,
        (slot.locationId ? locationDisplayNameById.get(slot.locationId) : undefined) ?? slot.locationName
      ),
      id: availabilitySlotKey(slot),
      displayOrder: index,
    }));
  }, [locationDisplayNameById, visibleSlots]);
  const visibleSlotByKey = useMemo(
    () => new Map(visibleSlots.map((slot) => [availabilitySlotKey(slot), slot])),
    [visibleSlots]
  );

  const selectedAppointmentLocation = useMemo(() => {
    if (!selectedSlot) {
      return schedulingMode === SchedulingMode.Location ? selectedLocation : null;
    }

    const slotLocation = selectedSlot.locationId
      ? locations.find((location) => location.locationId === selectedSlot.locationId)
      : undefined;

    return slotLocation ?? (schedulingMode === SchedulingMode.Location ? selectedLocation : null);
  }, [locations, schedulingMode, selectedLocation, selectedSlot]);

  const currentIndex = step === SchedulingStep.DONE ? -1 : SCHEDULING_STEPS.indexOf(step);
  // Gate on the sanitized reason, since one made only of stripped characters sanitizes to "" and
  // would be dropped before it ever reaches the API.
  const hasRequiredReasonForVisit = !requireReasonForVisit || sanitizeReasonForVisit(reasonForVisit).length > 0;
  const canContinue =
    step === SchedulingStep.DETAILS
      ? hasRequiredReasonForVisit && !eligibilityLoading
      : step === SchedulingStep.VISIT
        ? schedulingMode === SchedulingMode.Location
          ? selectedLocation !== null && (!checkSchedulingEligibility || providers.length > 0)
          : selectedProvider !== null
        : step !== SchedulingStep.TIME || (selectedSlot !== null && selectedAppointmentLocation !== null);

  const goNext = () => {
    if (currentIndex >= 0 && currentIndex < SCHEDULING_STEPS.length - 1) {
      setStep(SCHEDULING_STEPS[currentIndex + 1]);
    }
  };

  const goBack = () => {
    if (currentIndex > 0) {
      setStep(SCHEDULING_STEPS[currentIndex - 1]);
    }
  };

  const handleDetailsSubmit = async (values: PatientDetailsDraft) => {
    if (!hasRequiredReasonForVisit || eligibilityLoading) return;

    setDetails(values);

    setEligibilityLoading(true);
    setEligibilityError(null);
    setReferralWindow(null);
    setVerifiedPatientDetails(undefined);

    availabilityRequestId.current += 1;
    setAvailability(null);
    setAvailabilityLoading(false);
    setIsLoadingMore(false);
    setAvailabilityError(null);
    setLoadedBaseKey(null);
    setSelectedSlot(null);
    setLocationProviderFilterId(null);
    setWindowDays(INITIAL_WINDOW_DAYS);

    try {
      const patientDetails = buildIntakeRequest(values);
      const result = await checkOnlineSchedulingEligibility(
        {
          ...patientDetails,
          initialProviderId: initialProviderId || null,
          initialLocationId: initialLocationId || null,
          regionCode,
          reasonForVisit: sanitizeReasonForVisit(reasonForVisit),
        },
        tenant
      );

      setVerifiedPatientDetails(patientDetails);
      setReferralWindow(result.referralWindow ?? null);
      setIsReturningPatient(result.isReturningPatient);
      setLocations(result.locations);
      setProviders(result.providers);
      setSelectedLocation(findInitialLocation(result.locations));
      setSelectedProviderId(findInitialProvider(result.providers)?.practitionerId);

      if (!result.providers.length) {
        setEligibilityError(EligibilityError.Ineligible);
      } else if (!result.locations.length) {
        setEligibilityError(EligibilityError.Unavailable);
      }

      setStep(SchedulingStep.VISIT);
    } catch {
      setEligibilityError(EligibilityError.Unavailable);
    } finally {
      setEligibilityLoading(false);
    }
  };

  // Until the first fetch for the current scope resolves, show the picker's loading state rather
  // than a one-frame flash of the "no appointments" empty state on step entry.
  const availabilityBaseKey = availabilityScopeKey ? `${availabilityScopeKey}:${appointmentType}` : null;
  const availabilityPending =
    step === SchedulingStep.TIME && availabilityBaseKey !== null && loadedBaseKey !== availabilityBaseKey;

  // Stable handler so the picker's calendar effects don't re-run on every render.
  const handleSelectSlot = useCallback(
    (slot: NormalizedSlot | null) => {
      setSelectedSlot(slot ? (visibleSlotByKey.get(slot.id) ?? null) : null);
    },
    [visibleSlotByKey]
  );

  const handleLocationProviderFilterChange = useCallback((providerId: string | null) => {
    setLocationProviderFilterId(providerId);
    setSelectedSlot(null);
  }, []);

  const handleLoadMore = useCallback(() => {
    void loadAvailability(Math.min(windowDays + WINDOW_INCREMENT_DAYS, maxWindowDays), { more: true });
  }, [loadAvailability, windowDays, maxWindowDays]);

  function buildIntakeRequest(values: PatientDetailsDraft) {
    return onlineSchedulingIntakeSchema.parse({
      ...values,
      reasonForVisit,
      selfPay: allowSelfPay && values.selfPay === true,
      insurance:
        requireExistingPatient || (allowSelfPay && values.selfPay)
          ? undefined
          : { payer: values.insurancePayer, memberId: values.insuranceMemberId },
      guardian: isMinorDateOfBirth(values.dateOfBirth)
        ? { firstName: values.guardianFirstName, lastName: values.guardianLastName, phone: values.guardianPhone }
        : undefined,
    });
  }

  const confirmBooking = async (smsConsent: boolean) => {
    if (isBooking || !selectedAppointmentLocation || !selectedSlot) {
      return;
    }

    if (!selectedSlot.practitionerId) {
      setBookingError("This appointment is missing a provider. Please choose another time.");

      return;
    }

    setIsBooking(true);
    onBookingStateChange?.(true);
    setBookingError(null);

    try {
      const request: OnlineSchedulingBookRequest = {
        ...buildIntakeRequest(details),
        smsConsent,
        regionCode,
        location: selectedAppointmentLocation.locationId,
        appointmentSlot: {
          appointmentSlotActionId: selectedSlot.appointmentSlotActionId,
          appointmentId: selectedSlot.appointmentId,
          appointmentType: selectedSlot.appointmentType as AvailabilityAppointmentType,
          ehrAppointmentTypeId: selectedSlot.ehrAppointmentTypeId,
          start: selectedSlot.start,
          end: selectedSlot.end,
          practitionerId: selectedSlot.practitionerId,
          locationId: selectedAppointmentLocation.locationId,
        },
      };

      const result = await bookOnlineSchedulingAppointment(request, tenant);

      setBookingResult(result);
      setStep(SchedulingStep.DONE);
    } catch {
      setBookingError("Please choose another time, or call the clinic to book your visit.");
    } finally {
      setIsBooking(false);
      onBookingStateChange?.(false);
    }
  };

  const resetFlow = () => {
    setBookingResult(null);
    setBookingError(null);
    setSelectedSlot(null);
    setDetails(EMPTY_PATIENT_DETAILS);
    setReasonForVisit("");
    setIsReturningPatient(false);
    const initialLocation = findInitialLocation(initialLocations);
    const initialProvider = findInitialProvider(initialProviders);

    setSelectedLocation(initialLocation);
    setSelectedProviderId(initialProvider?.practitionerId);
    setLocationProviderFilterId(null);
    setSchedulingMode(initialProviderId ? SchedulingMode.Provider : SchedulingMode.Location);
    setEligibilityError(null);
    setLocations(initialLocations);
    setProviders(initialProviders);
    setStep(SCHEDULING_STEPS[0]);
  };

  // Success screen.
  if (step === SchedulingStep.DONE && bookingResult && selectedAppointmentLocation && selectedSlot) {
    return (
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="pt-6">
          <SuccessStep
            location={selectedAppointmentLocation}
            slotStart={selectedSlot.start}
            providerDisplayName={bookingResult.confirmedAppointment.providerDisplayName}
            patientName={details.firstName.trim() || "there"}
            onBookAnother={resetFlow}
          />
        </CardContent>
      </Card>
    );
  }

  const optionsEmpty = locations.length === 0 || providers.length === 0;

  if (optionsLoading || optionsError || (optionsEmpty && !checkSchedulingEligibility)) {
    return (
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="pt-6">
          {optionsLoading ? (
            <div className="space-y-4 py-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-2">
              <SchedulingErrorAlert clinicPhoneNumber={clinicPhoneNumber} />
              <Button variant="outline" onClick={() => void loadOptions()}>
                Try again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const title = step !== SchedulingStep.DONE ? STEP_TITLE[step] : null;

  return (
    <div className="space-y-6">
      <ProgressSteps current={step === SchedulingStep.DONE ? SchedulingStep.REVIEW : step} />

      <ValerieCard
        title={title ?? "Appointment confirmed"}
        className="rounded-2xl shadow-sm"
        contentClassName="space-y-6"
        actions={
          step === SchedulingStep.VISIT && !lockProvider ? (
            <SchedulingModeToggle
              value={schedulingMode}
              onChange={(mode) => {
                setSchedulingMode(mode);
                setLocationProviderFilterId(null);
              }}
            />
          ) : undefined
        }
      >
        {(step === SchedulingStep.DETAILS || step === SchedulingStep.VISIT) && eligibilityError && (
          <div className="space-y-2" aria-live="polite">
            <SchedulingErrorAlert
              clinicPhoneNumber={clinicPhoneNumber}
              isIneligible={eligibilityError === EligibilityError.Ineligible}
            />
          </div>
        )}
        {step === SchedulingStep.VISIT && (
          <div className="space-y-6">
            {schedulingMode === SchedulingMode.Location ? (
              <LocationSelectionField
                locations={locations}
                selected={selectedLocation}
                onChange={setSelectedLocation}
              />
            ) : (
              <ProviderSelectionField
                providers={providers}
                selectedProviderId={selectedProviderId}
                onChange={(providerId) => setSelectedProviderId(providerId ?? undefined)}
                readOnly={lockProvider}
              />
            )}
          </div>
        )}

        {step === SchedulingStep.TIME && availabilityScopeKey && (
          <div className="space-y-5">
            {schedulingMode === SchedulingMode.Location && selectedLocation && (
              <div className="space-y-3">
                <LocationSelectionField
                  locations={locations}
                  selected={selectedLocation}
                  onChange={setSelectedLocation}
                  readOnly
                />
                {locationProviderOptions.length > 1 && (
                  <ProviderSelectionField
                    providers={locationProviderOptions}
                    selectedProviderId={locationProviderFilterId}
                    onChange={handleLocationProviderFilterChange}
                    allowAny
                  />
                )}
              </div>
            )}
            {schedulingMode === SchedulingMode.Provider && selectedProvider && (
              <ProviderSelectionField
                providers={providers}
                selectedProviderId={selectedProvider.practitionerId}
                onChange={(providerId) => setSelectedProviderId(providerId ?? undefined)}
                readOnly
              />
            )}

            {referralWindow?.isUrgent && (
              <Alert>
                <AlertTitle>Urgent referral</AlertTitle>
                <AlertDescription>
                  Your referral is marked urgent, so we only show appointments within the next{" "}
                  {referralWindow.maxWindowDays} days, starting tomorrow. Same-day appointments cannot be booked online.
                  If no available time works for you, please call the clinic for help scheduling your urgent referral.
                </AlertDescription>
              </Alert>
            )}
            {availabilityError ? (
              <SchedulingErrorAlert clinicPhoneNumber={clinicPhoneNumber} />
            ) : (
              <AppointmentSlotPicker
                slots={normalizedSlots}
                isLoading={availabilityLoading || availabilityPending}
                selectedSlotId={selectedSlot ? availabilitySlotKey(selectedSlot) : null}
                onSelectSlot={handleSelectSlot}
                onLoadMore={handleLoadMore}
                isLoadingMore={isLoadingMore}
                canLoadMore={windowDays < maxWindowDays}
                windowDays={windowDays}
                maxWindowDays={maxWindowDays}
                slotSecondaryLabel={schedulingMode === SchedulingMode.Provider ? "location" : "provider"}
                showProviderSummary={schedulingMode === SchedulingMode.Location && locationProviderOptions.length <= 1}
                emptyHint={
                  referralWindow?.isUrgent
                    ? "No appointments match within your urgent referral window. Please call the clinic for help scheduling."
                    : schedulingMode === SchedulingMode.Provider && !lockProvider
                      ? "Try searching further out, or go back to choose a different provider."
                      : undefined
                }
                showPreferredBadge={visibleSlots.some((slot) => slot.preferenceTier)}
              />
            )}
          </div>
        )}

        {step === SchedulingStep.DETAILS && (
          <fieldset disabled={eligibilityLoading} aria-busy={eligibilityLoading} className="space-y-6">
            <VisitStep
              reasonForVisit={reasonForVisit}
              onChangeReason={setReasonForVisit}
              required={requireReasonForVisit}
            />
            <div className="border-t pt-6">
              <DetailsStep
                formId={DETAILS_FORM_ID}
                defaultValues={details}
                onSubmit={(values) => handleDetailsSubmit(values)}
                requireInsurance={requireInsurance}
                allowSelfPay={allowSelfPay}
                requireExistingPatient={requireExistingPatient}
                tenant={tenant}
              />
            </div>
          </fieldset>
        )}

        {step === SchedulingStep.REVIEW && selectedAppointmentLocation && selectedSlot && (
          <ReviewStep
            location={selectedAppointmentLocation}
            isReturningPatient={isReturningPatient}
            reasonForVisit={reasonForVisit}
            slot={selectedSlot}
            details={details}
            onBack={goBack}
            onConfirm={confirmBooking}
            isBooking={isBooking}
            bookingError={bookingError}
            clinicPhoneNumber={clinicPhoneNumber}
          />
        )}

        {step !== SchedulingStep.DONE && step !== SchedulingStep.REVIEW && (
          <div className="border-t pt-4">
            <StepActions
              primaryLabel={eligibilityLoading ? "Checking your details..." : "Continue"}
              isBusy={eligibilityLoading}
              primaryType={step === SchedulingStep.DETAILS ? "submit" : "button"}
              form={step === SchedulingStep.DETAILS ? DETAILS_FORM_ID : undefined}
              onPrimary={step === SchedulingStep.DETAILS ? undefined : goNext}
              primaryDisabled={!canContinue || eligibilityLoading}
              onBack={currentIndex > 0 ? goBack : undefined}
            />
          </div>
        )}
      </ValerieCard>
    </div>
  );
}

function SchedulingModeToggle({
  value,
  onChange,
}: {
  value: SchedulingMode;
  onChange: (mode: SchedulingMode) => void;
}) {
  return (
    <Tabs
      value={value}
      onValueChange={(mode) => {
        if (mode === SchedulingMode.Location || mode === SchedulingMode.Provider) {
          onChange(mode);
        }
      }}
    >
      <TabsList aria-label="Choose how to find an appointment">
        <TabsTrigger value={SchedulingMode.Location}>By location</TabsTrigger>
        <TabsTrigger value={SchedulingMode.Provider}>By provider</TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
